import { useState, useEffect } from 'react'
import dayjs from 'dayjs'
import { CalendarIcon } from '@radix-ui/react-icons'
import { format } from 'date-fns'

import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import { Input } from '@/components/ui/input'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { useRecoveryStore } from '@/stores/recovery-store'
import { toast } from 'sonner'

const RecoveryDatePicker = ({ className }) => {
  const { startDate, endDate, setStartDate, setEndDate, setDateRange } =
    useRecoveryStore()

  const from = dayjs(startDate).toDate()
  const to = dayjs(endDate).toDate()

  const [displayStartDate, setDisplayStartDate] = useState(
    dayjs(startDate).format('YYYY-MM-DD'),
  )
  const [displayEndDate, setDisplayEndDate] = useState(
    dayjs(endDate).format('YYYY-MM-DD'),
  )

  useEffect(() => {
    setDisplayStartDate(dayjs(startDate).format('YYYY-MM-DD'))
  }, [startDate])

  useEffect(() => {
    setDisplayEndDate(dayjs(endDate).format('YYYY-MM-DD'))
  }, [endDate])

  const handleSelectDate = (date) => {
    if (date?.from && date?.to) {
      setDateRange(
        dayjs(date.from).format('YYYYMMDD'),
        dayjs(date.to).format('YYYYMMDD'),
      )
    }
  }

  const handleStartDateChange = (e) => {
    const value = e.target.value
    setDisplayStartDate(value)
    if (dayjs(value, 'YYYY-MM-DD', true).isValid()) {
      setStartDate(dayjs(value).format('YYYYMMDD'))
    } else if (value !== '') {
      toast.error('Invalid Start Date', {
        description: 'Please enter a valid date in YYYY-MM-DD format.',
      })
    }
  }

  const handleEndDateChange = (e) => {
    const value = e.target.value
    setDisplayEndDate(value)
    const today = dayjs().endOf('day')
    if (dayjs(value, 'YYYY-MM-DD', true).isValid()) {
      if (dayjs(value).isAfter(today)) {
        toast.error('Invalid End Date', {
          description: 'End date cannot be in the future.',
        })
      } else if (dayjs(value).isBefore(dayjs(startDate))) {
        toast.error('Invalid End Date', {
          description: 'End date cannot be before start date.',
        })
      } else {
        setEndDate(dayjs(value).format('YYYYMMDD'))
      }
    } else if (value !== '') {
      toast.error('Invalid End Date', {
        description: 'Please enter a valid date in YYYY-MM-DD format.',
      })
    }
  }

  const date = { from, to }

  return (
    <div className={cn('grid gap-2', className)}>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            id="date"
            variant={'outline'}
            className={cn(
              'w-[300px] justify-start text-left font-normal',
              !date && 'text-muted-foreground',
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {date?.from ? (
              date.to ? (
                <>
                  {format(date.from, 'LLL dd, y')} -{' '}
                  {format(date.to, 'LLL dd, y')}
                </>
              ) : (
                format(date.from, 'LLL dd, y')
              )
            ) : (
              <span>Pick a date</span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            initialFocus
            mode="range"
            defaultMonth={date?.from}
            selected={date}
            onSelect={handleSelectDate}
            numberOfMonths={2}
          />
          <div className="flex flex-row items-center justify-between gap-2 p-4">
            <div className="flex items-center gap-2">
              <Input
                id="startDate"
                value={displayStartDate}
                onChange={handleStartDateChange}
                placeholder="YYYY-MM-DD"
                className="flex-1"
              />
            </div>
            <div className="flex items-center gap-2">
              <Input
                id="endDate"
                value={displayEndDate}
                onChange={handleEndDateChange}
                placeholder="YYYY-MM-DD"
                className="flex-1"
              />
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  )
}

export default RecoveryDatePicker

